import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mailbox, Loader2, Copy, Check, Send, Link2, Search, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  created: ['Link created', 'bg-blue-100 text-blue-700'],
  opened: ['Opened', 'bg-amber-100 text-amber-800'],
  claimed: ['Claimed & paid', 'bg-[#00A79D]/15 text-[#00857D]'],
  live: ['Live', 'bg-emerald-100 text-emerald-700'],
};

const fullLink = (slug, token) => `${window.location.origin}/claim/${slug}?invite=${token}`;

const StatCard = ({ label, value, testid }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid={testid}>
    <div className="text-2xl font-heading font-bold text-gray-900">{value}</div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
  </div>
);

const SupplierRow = ({ row, onCreated }) => {
  const [email, setEmail] = useState(row.invite?.email || '');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const inv = row.invite;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/supplier-invites`, { company_id: row.company_id, email: email || null });
      toast.success('Invite link created');
      onCreated(row.company_id, data);
    } catch { toast.error('Could not create invite'); } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullLink(inv.slug, inv.token));
      setCopied(true); setTimeout(() => setCopied(false), 1600);
      toast.success('Link copied');
    } catch { toast.error('Copy failed'); }
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/supplier-invites/${inv.id}/send`);
      if (data.sent) toast.success('Invite email sent');
      else if (data.reason === 'no_email') toast.error('Add an email first, then create the invite');
      else toast.info(data.message || 'Email sending is on hold; copy the link instead');
    } catch { toast.error('Send failed'); } finally { setBusy(false); }
  };

  const st = inv && STATUS_STYLE[inv.status];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`invite-row-${row.company_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-[200px]">
          <h3 className="font-heading font-bold text-gray-900">{row.name}</h3>
          {row.website ? <a href={row.website} target="_blank" rel="noreferrer" className="text-xs text-[#00857D] hover:underline break-all">{row.website}</a>
            : <span className="text-xs text-gray-400">No website on file</span>}
        </div>
        {inv && <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0 ${st[1]}`} data-testid={`invite-status-${row.company_id}`}>{st[0]}</span>}
      </div>

      {!inv ? (
        <div className="flex gap-2 mt-3 flex-wrap">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Contact email (optional)"
            data-testid={`invite-email-${row.company_id}`} className="flex-1 min-w-[180px] border border-gray-200 rounded-md px-3 py-2 text-sm" />
          <button onClick={create} disabled={busy} data-testid={`invite-create-${row.company_id}`}
            className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Create invite link
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2 items-center flex-wrap">
            <input readOnly value={fullLink(inv.slug, inv.token)} data-testid={`invite-link-${row.company_id}`}
              className="flex-1 min-w-[220px] border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-xs text-gray-600" />
            <button onClick={copy} data-testid={`invite-copy-${row.company_id}`}
              className="inline-flex items-center gap-1.5 border border-gray-200 text-gray-700 text-sm font-semibold px-3 py-2 rounded-md hover:bg-gray-50">
              {copied ? <Check className="w-4 h-4 text-[#00A79D]" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Contact email"
              data-testid={`invite-email-${row.company_id}`} className="flex-1 min-w-[180px] border border-gray-200 rounded-md px-3 py-2 text-sm" />
            <button onClick={create} disabled={busy} className="text-sm text-gray-600 border border-gray-200 px-3 py-2 rounded-md hover:bg-gray-50">Save email</button>
            <button onClick={send} disabled={busy} data-testid={`invite-send-${row.company_id}`}
              className="inline-flex items-center gap-1.5 border border-[#00A79D] text-[#00857D] text-sm font-semibold px-3 py-2 rounded-md hover:bg-[#00A79D]/5 disabled:opacity-60">
              <Send className="w-4 h-4" /> Send email
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SupplierInvites = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [unclaimed, setUnclaimed] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  const loadSummary = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/supplier-invites`); setCounts(data.counts); setUnclaimed(data.unclaimed); }
    catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/supplier-invites/suppliers`, { params: { search: q || undefined, filter: filter === 'all' ? undefined : filter, page, page_size: pageSize } });
      setRows(data.items); setTotal(data.total);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [q, filter, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(); }, [load]);

  const onCreated = (companyId, invite) => {
    setRows((rs) => rs.map((r) => r.company_id === companyId ? { ...r, invite } : r));
    loadSummary();
  };

  const runSearch = () => { setPage(1); setQ(search); };
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="supplier-invites">
      <div className="flex items-center gap-2 mb-1"><Mailbox className="w-6 h-6 text-[#00A79D]" /><h1 className="font-heading text-2xl font-bold text-gray-900">Supplier Invites</h1></div>
      <p className="text-gray-500 text-sm mb-4">Invite unclaimed suppliers to claim their listing. Enter a contact email to send, or copy a tracked link to send yourself.</p>

      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-lg px-4 py-3 mb-5" data-testid="invite-email-notice">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Email sending is on hold until your Resend sending domain is verified. You can still create and copy tracked invite links to send yourself right now.</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Unclaimed suppliers" value={unclaimed} testid="stat-unclaimed" />
        <StatCard label="Links created" value={counts.created ?? 0} testid="stat-created" />
        <StatCard label="Opened" value={counts.opened ?? 0} testid="stat-opened" />
        <StatCard label="Claimed & paid" value={counts.claimed ?? 0} testid="stat-claimed" />
        <StatCard label="Live" value={counts.live ?? 0} testid="stat-live" />
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runSearch()}
            placeholder="Search unclaimed suppliers..." data-testid="invite-search"
            className="w-full border border-gray-200 rounded-md pl-9 pr-3 py-2 text-sm" />
        </div>
        <button onClick={runSearch} className="bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-4 py-2 rounded-md">Search</button>
        <select value={filter} onChange={(e) => { setPage(1); setFilter(e.target.value); }} data-testid="invite-filter" className="border border-gray-200 rounded-md px-3 py-2 text-sm">
          <option value="all">All</option>
          <option value="not_invited">Not invited</option>
          <option value="invited">Invited</option>
        </select>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>
        : rows.length === 0 ? <div className="py-16 text-center text-gray-400" data-testid="invite-empty">No suppliers match.</div>
        : (
          <div className="space-y-3">
            {rows.map((r) => <SupplierRow key={r.company_id} row={r} onCreated={onCreated} />)}
          </div>
        )}

      <div className="flex items-center justify-between mt-6">
        <span className="text-xs text-gray-500">{total} unclaimed suppliers</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="invite-prev" className="text-sm border border-gray-200 px-3 py-1.5 rounded-md disabled:opacity-40">Prev</button>
          <span className="text-sm text-gray-600">{page} / {pages}</span>
          <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} data-testid="invite-next" className="text-sm border border-gray-200 px-3 py-1.5 rounded-md disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
};

export default SupplierInvites;
